import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import Icon from "@/components/Icon";
import { NSlider } from "@/components/NSlider";
import TextArea from "@/components/TextArea";
import { GenMultiSelector } from "@/modules/editor/components/common/GenMultiSelector";
import { SizeInput } from "@/modules/editor/components/common/SizeInput";
import { NegativePromptSwitch } from "@/modules/editor/components/NegativePromptConfig";
import { InputField } from "@/modules/editor/components/ParamInputField";
import { LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { GeneratePriceRules } from "@/modules/editor/hooks/useGenerateCost";
import { uiStore } from "@/stores/ui";
import { userAssetsStore } from "@/stores/userAssets";
import events, { MessageType } from "@/utils/events";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import { useOwnToast, toast } from "@/utils/misc";
import { translateAndCheck } from "@/utils/prompt";
import { Box, chakra, Flex, Text, useBoolean } from "@chakra-ui/react";
import { langStore } from "@noli/business";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Monkey_Words } from "../lang";
import { monkeyStore } from "../store";
import { ReactComponent as robot } from "@/assets/icons/animate_robot.svg";

function MonkeyPanel(props: { src?: string }) {
  const { src } = props;
  const { translate } = useTranslate();

  useEffect(() => {
    userAssetsStore.fetchHeartCoin();
  }, []);

  useEffect(() => {
    monkeyStore.setUrl(src ?? "");
    if (uiStore.leftPanel === "img2img") monkeyStore.setTaskType("img2img");
    if (uiStore.leftPanel === "txt2img") monkeyStore.setTaskType("txt2img");
  }, [uiStore.leftPanel, src]);

  const lang = langStore.tgt;
  const t = useOwnToast();
  const [isLoading, loadingOp] = useBoolean(false);

  async function onSubmit(): Promise<void> {
    // sanity check
    if (!monkeyStore.prompt) {
      toast(t, "warning", translate(Editor_Words["no-text-input-message"]));
      return;
    }
    if (monkeyStore.taskType === "img2img" && !monkeyStore.url) {
      toast(t, "warning", translate(Editor_Words["no-text-input-message"]));
      return;
    }

    // if (!affordable) {
    //   events.emit(MessageType.HEART_COIN);
    //   return;
    // }
    loadingOp.on();
    if (!(await translateAndCheck(monkeyStore.prompt, t, lang, false, false))) {
      loadingOp.off();
      return;
    }
    loadingOp.off();
    await monkeyStore.postTask({
      t,
      lang,
    });
  }

  const disabled = useMemo(() => {
    if (monkeyStore.taskType === "img2img") {
      return !src || isLoading;
    }
    return isLoading;
  }, [isLoading, monkeyStore.taskType, src]);

  return (
    <Box w="100%" pos="relative">
      <LabelText display="flex" justifyContent="space-between">
        <Text>{translate(Editor_Words["prompt-input-title"])}：</Text>
        <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
          <Text
            border="1px solid"
            pos="relative"
            px="10px"
            bg="#323232"
            borderRadius="4px"
            color="#b3b3b3"
            cursor="pointer"
            zIndex={1}
            _hover={{ color: "white" }}
          >
            {translate(Editor_Words["prompt-expand-modal-title"])}
          </Text>
          <Text
            pos="absolute"
            px="6px"
            right={0}
            bottom="100%"
            bg="#36C19E"
            color="white"
            borderRadius="3px"
            fontSize="12px"
            _after={{
              content: "''",
              bg: "#36c19e",
              w: "10px",
              h: "10px",
              pos: "absolute",
              right: 0,
              top: "80%",
            }}
          >
            new
          </Text>
        </Text>
      </LabelText>
      <Box h="fit-content" pos="relative" my="16px">
        <TextArea
          w="100%"
          my="0px"
          lang={lang}
          textAreaH={300}
          minRows={5}
          maxRows={10}
          placeholderWord={Monkey_Words["textarea-placeholder"]}
          text={monkeyStore.prompt}
          setText={monkeyStore.setPrompt}
        />
        {monkeyStore.promptExpanding && (
          <Flex
            w="100%"
            h="100%"
            pos="absolute"
            top="0"
            left="0"
            flexGrow={1}
            bg="#323232"
            borderRadius="6px"
            border="1px solid white"
            justify="center"
            flexDir="column"
            align="center"
          >
            <Icon w="32px" h="32px" svg={robot} />
            <Text color="#b3b3b3">提示词生成中</Text>
          </Flex>
        )}
      </Box>

      <NegativePromptSwitch
        value={monkeyStore.negativeSwitch}
        onChange={monkeyStore.setNegativeVisible}
      />
      {monkeyStore.negativeSwitch && (
        <TextArea
          w="100%"
          mb="16px"
          lang={lang}
          textAreaH={300}
          minRows={5}
          maxRows={10}
          placeholderWord={Editor_Words["negative-prompt-placeholder"]}
          text={monkeyStore.negativePrompt}
          setText={monkeyStore.setNegativePrompt}
        />
      )}
      {monkeyStore.taskType === "img2img" && (
        <NSlider
          mt="16px"
          value={monkeyStore.imageCfgScale}
          max={1}
          min={0}
          step={0.1}
          label={translate(Editor_Words["fidelity"])}
          onChange={monkeyStore.setImageCfgScale}
        />
      )}
      {/* <NSlider
        mt="16px"
        value={monkeyStore.cfgScale}
        max={30}
        min={-20}
        step={1}
        label={translate(Editor_Words["guidance-scale-caption"])}
        onChange={monkeyStore.setCfgScale}
      />
      <NSlider
        mt="16px"
        value={monkeyStore.samplingStep}
        max={100}
        min={5}
        step={1}
        label={translate(Editor_Words["num-steps-caption"])}
        onChange={monkeyStore.setSamplingStep}
      /> */}

      <InputField
        mt="16px"
        lang={lang}
        identifier="advance-mode-seed"
        word={Editor_Words["seed-caption"]}
        value={monkeyStore.seed}
        setValue={monkeyStore.setSeed}
        lower={-1}
      />
      <SizeInput
        mt="16px"
        defaultW={monkeyStore.width}
        defaultH={monkeyStore.height}
        setW={monkeyStore.setW}
        setH={monkeyStore.setH}
      />

      <GenMultiSelector
        mt="16px"
        autoDisable={false}
        value={monkeyStore.batchSize}
        onChange={monkeyStore.setBatchSize}
      />

      <BizGenerateButton
        mt="24px"
        sticky
        size={{ w: monkeyStore.width, h: monkeyStore.height }}
        calcPriceProps={GeneratePriceRules.Monkey}
        generateCount={monkeyStore.batchSize}
        showCost
        useCoin="payed"
        buttonProps={{
          isLoading,
          isDisabled: disabled,
        }}
        onSubmit={onSubmit}
      >
        <chakra.span fontWeight={600} fontSize="13px">
          {translate(Monkey_Words["submit"])}
        </chakra.span>
      </BizGenerateButton>
    </Box>
  );
}
export default observer(MonkeyPanel);
